import { Box, TextField, Typography } from "@material-ui/core";
import * as React from "react";

import { AuthErrorSummary } from "@/components/AuthFeedback";
import { BottomSheet } from "@/components/BottomSheet";
import { CustomButton } from "@/components/Button";
import { updateEmailSchema } from "@/validations/settings";

import { useSettingsUserUpdate } from "../../hooks/useSettingsUserUpdate";
import { SettingItemsProps } from "../../SettingsPage";
import { SettingsDestination, SettingsHelper, SettingsSuccessMark, useSettingsContentStyles } from "../SettingsContent";
import { SettingsFormRow } from "../SettingsFormRow";

export const UpdateEmail: React.FC<SettingItemsProps> = ({ user, setUser, onBack }) => {
  const classes = useSettingsContentStyles();
  const [email, setEmail] = React.useState("");
  const [fieldError, setFieldError] = React.useState("");
  const [sentEmail, setSentEmail] = React.useState<string | null>(null);
  const [editing, setEditing] = React.useState(false);
  const { update, loading, error } = useSettingsUserUpdate(setUser);
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    const result = updateEmailSchema.safeParse({ email });
    if (!result.success) {
      setFieldError(result.error.issues[0].message);
      return;
    }
    setFieldError("");
    const response = await update({ email });
    if (response) {
      setSentEmail(response.email);
    }
  };
  if (sentEmail !== null)
    return (
      <>
        <Box className={classes.stack}>
          <SettingsSuccessMark />
          <Box>
            <Typography component="h2" className={classes.heading}>
              確認メールを送信しました
            </Typography>
            <Box mt={1}>
              <Typography className={classes.description}>
                確認メールのリンクを開いて、変更を完了してください。
              </Typography>
            </Box>
          </Box>
          <SettingsDestination label="送り先" value={sentEmail} />
          <Box className={classes.note}>
            <Typography className={classes.noteHeading}>届かないときは</Typography>
            <Typography className={classes.caption}>迷惑メールのフォルダをご確認ください。</Typography>
            <Typography className={classes.caption}>
              見つからない場合は、もう一度メールアドレスの変更をお試しください。
            </Typography>
          </Box>
        </Box>
        <Box className={classes.actions}>
          <CustomButton buttonSize="L" tone="outline" onClick={onBack}>
            設定に戻る
          </CustomButton>
        </Box>
      </>
    );
  return (
    <form className={classes.flow} noValidate onSubmit={submit}>
      <Box className={classes.stack}>
        <SettingsDestination label="いまのメールアドレス" value={user.email} />
        <Box>
          <SettingsFormRow
            label="新しいメールアドレス"
            value={email}
            placeholder="未入力"
            error={fieldError}
            disabled={loading}
            onClick={() => setEditing(true)}
          />
          <Box mt={1}>
            <SettingsHelper>Apple や Google で登録した方は、メールアドレスを変えられません。</SettingsHelper>
          </Box>
        </Box>
      </Box>
      <Box className={classes.actions}>
        <AuthErrorSummary message={error} />
        <CustomButton buttonSize="L" type="submit" disabled={loading}>
          {loading ? "送信しています…" : "確認メールを送る"}
        </CustomButton>
      </Box>
      <BottomSheet
        open={editing}
        title="新しいメールアドレス"
        onOpen={() => undefined}
        onClose={() => setEditing(false)}
        footer={
          <CustomButton buttonSize="L" onClick={() => setEditing(false)}>
            決定する
          </CustomButton>
        }
      >
        <TextField
          label="新しいメールアドレス"
          variant="outlined"
          fullWidth
          autoFocus
          autoComplete="email"
          type="email"
          value={email}
          onChange={(event) => {
            setEmail(event.target.value);
            setFieldError("");
          }}
        />
      </BottomSheet>
    </form>
  );
};
